import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { getProfilePicture } from '../utils/profilePicture';
import '../App.css';

interface PlayerSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (player: { id: string; firstName: string; lastName: string; email: string }) => void;
}

interface PlayerResult {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  profilePicture?: string;
  profileType?: string;
}

const PlayerSearchDialog: React.FC<PlayerSearchDialogProps> = ({ isOpen, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<PlayerResult[]>([]);
  const [loading, setLoading] = useState(false);

  const searchPlayers = useCallback(async (term: string) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/players', { params: { search: term, limit: '20' } });
      const players = response.data.players || response.data || [];
      setResults(players);
    } catch (err) {
      console.error('Error searching players:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchPlayers(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchPlayers]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setResults([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '30px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>Find Player</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#757575',
              padding: '5px'
            }}
          >
            x
          </button>
        </div>

        <div className="form-group" style={{ marginBottom: '15px' }}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', minHeight: '200px', maxHeight: '400px' }}>
          {loading && <p style={{ textAlign: 'center', color: '#757575' }}>Searching...</p>}

          {!loading && searchTerm.length < 2 && (
            <p style={{ textAlign: 'center', color: '#757575' }}>Type at least 2 characters to search</p>
          )}

          {!loading && searchTerm.length >= 2 && results.length === 0 && (
            <p style={{ textAlign: 'center', color: '#757575' }}>No players found</p>
          )}

          {results.map(player => (
            <div
              key={player.id}
              onClick={() => {
                onSelect({
                  id: player.id,
                  firstName: player.firstName,
                  lastName: player.lastName,
                  email: player.email || ''
                });
                onClose();
              }}
              style={{
                padding: '12px 15px',
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
            >
              <img
                src={getProfilePicture(player)}
                alt={`${player.firstName} ${player.lastName}`}
                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: '500' }}>
                  {player.firstName} {player.lastName}
                </div>
                <div style={{ fontSize: '13px', color: '#757575', marginTop: '2px' }}>
                  {player.email || 'No email'}
                  {player.profileType && (
                    <span className="badge badge-info" style={{ marginLeft: '8px', fontSize: '11px' }}>
                      {player.profileType}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '15px' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default PlayerSearchDialog;
